import { useCallback, useRef, useState } from 'react';
import api from '@/services/api';

const REQUEST_TIMEOUT = 45000; // OpenAI síncrona estoura o default de 15s do axios

export const getUserLearningPathIdFromPathname = pathname => {
  const match = /^\/trilha\/atual\/(\d+)/.exec(pathname);
  return match ? Number(match[1]) : null;
};

// Acima dessa fração da cota diária o chat avisa o aluno de quanto já usou.
export const QUOTA_WARNING_THRESHOLD = 0.7;

export const parseQuota = meta => {
  const limit = Number(meta?.['daily-limit']);
  if (!limit || limit <= 0) return null;
  const used = Number(meta['daily-used']) || 0;
  return { limit, used, remaining: Math.max(Number(meta['daily-remaining'] ?? limit - used), 0) };
};

export const shouldWarnQuota = quota => !!quota && quota.remaining > 0 && quota.used / quota.limit > QUOTA_WARNING_THRESHOLD;

const parseResource = resource => ({
  id: Number(resource.id),
  role: resource.attributes.role,
  body: resource.attributes.body,
  createdAt: resource.attributes['created-at']
});

export function useTiaChat({ userLearningPathId = null, currentPath = null } = {}) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | thinking | error | limit
  const [quota, setQuota] = useState(null); // { limit, used, remaining } do dia, vindo do meta da API
  const retryIdRef = useRef(null);
  const lastBodyRef = useRef(null);
  const localSeqRef = useRef(0);
  const inFlightRef = useRef(false);

  // Resposta sem meta (API antiga) mantém a última cota conhecida.
  const updateQuota = useCallback(meta => {
    const next = parseQuota(meta);
    if (next) setQuota(next);
  }, []);

  const loadHistory = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await api.get('tia_chat/messages');
      setMessages(response.data.map(parseResource).reverse());
      updateQuota(response.meta);
    } catch {
      // histórico indisponível não bloqueia o chat — começa vazio
      setMessages([]);
    } finally {
      setStatus('idle');
    }
  }, [updateQuota]);

  const deliver = useCallback(
    async (attributes, optimistic) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (attributes.body !== undefined) {
        lastBodyRef.current = attributes.body;
        retryIdRef.current = null; // um envio novo (ou retry-por-body) supersede retry pendente
      }
      if (optimistic) setMessages(prev => [...prev, optimistic]);
      setStatus('thinking');
      try {
        const response = await api.post('tia_chat/messages', { data: { type: 'tia-chat-messages', attributes } }, { timeout: REQUEST_TIMEOUT });
        retryIdRef.current = null;
        lastBodyRef.current = null;
        setMessages(prev => [...prev, parseResource(response.data)]);
        updateQuota(response.meta);
        setStatus('idle');
      } catch (error) {
        updateQuota(error.response?.data?.meta);
        if (error.response?.status === 429) {
          setStatus('limit');
          return;
        }
        if (error.response?.status === 422) {
          retryIdRef.current = null;
          lastBodyRef.current = null;
          loadHistory();
          return;
        }
        const sentRetryId = attributes['retry-message-id'] ?? null;
        retryIdRef.current = error.response?.data?.['student-message-id'] ?? sentRetryId;
        setStatus('error');
      } finally {
        inFlightRef.current = false;
      }
    },
    [loadHistory, updateQuota]
  );

  const send = useCallback(
    body => {
      const attributes = { body };
      if (userLearningPathId) attributes['user-learning-path-id'] = userLearningPathId;
      if (currentPath) attributes['current-path'] = currentPath;
      localSeqRef.current += 1;
      deliver(attributes, { id: `local-${localSeqRef.current}`, role: 'student', body });
    },
    [deliver, userLearningPathId, currentPath]
  );

  const retry = useCallback(() => {
    if (retryIdRef.current) {
      deliver({ 'retry-message-id': retryIdRef.current }, null);
    } else if (lastBodyRef.current) {
      // falha antes do servidor salvar (rede): reenvia o corpo, sem otimista duplicado
      const attributes = { body: lastBodyRef.current };
      if (currentPath) attributes['current-path'] = currentPath;
      deliver(attributes, null);
    }
  }, [deliver, currentPath]);

  return { messages, status, quota, send, retry, loadHistory };
}
